import type { CSSProperties, ReactNode, Ref } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { addPhase, nextPhaseId, reorderPhase } from '../builder/draftOps';
import type { BuilderSelection } from '../builder/selection';
import { UNASSIGNED_PHASE_ID } from '../builder/types';
import type { DraftPhase } from '../builder/types';
import { usePhaseAccents, usePhaseStripPhases } from '../builder/usePhaseStrip';
import { phaseAccentClass } from './phaseAccent';

/**
 * The phase order, above the canvas.
 *
 * Phase order *is* board column order (FR-043), so it has to be editable
 * somewhere — and the canvas is where a user can see the process, which makes
 * it the natural place to say what order its stages come in.
 *
 * This module is the strip without the drag: the frame, one tab, and the arrow
 * buttons. `PhaseStripSortable` adds dragging on top of these same pieces, and
 * this plain version is what renders until that chunk arrives (see
 * `PhaseStripLazy`). Keeping `@dnd-kit/*` out of *this* file is what keeps it
 * out of the entry bundle — the builder route is eagerly imported, so anything
 * reachable from here ships to every route (constitution Principle 6).
 *
 * The arrows stay, and stay in tab order, even once a drag exists. They are the
 * keyboard path: a pointer gesture is not one, and hiding a control behind
 * hover would take it away from exactly the user it is there for. They are
 * merely quiet until the tab is hovered or holds focus.
 */

export interface PhaseStripProps {
  selection: BuilderSelection;
  onSelect: (selection: BuilderSelection) => void;
}

/** The drag wiring a sortable tab supplies; absent on the plain strip. */
export interface PhaseTabDrag {
  ref: Ref<HTMLSpanElement>;
  style?: CSSProperties;
  listeners?: Record<string, unknown>;
  isDragging?: boolean;
}

/**
 * A single phase tab. The whole tab is the drag handle when one is wired, so
 * the arrows stop the pointer from reaching it — an arrow press is a press, not
 * the beginning of a gesture.
 */
export function PhaseTab({
  phase,
  index,
  count,
  accentIndex,
  selected,
  onSelect,
  onMove,
  drag,
}: {
  phase: DraftPhase;
  index: number;
  count: number;
  accentIndex: number;
  selected: boolean;
  onSelect: () => void;
  onMove: (toIndex: number) => void;
  drag?: PhaseTabDrag;
}) {
  return (
    <span
      ref={drag?.ref}
      style={drag?.style}
      {...drag?.listeners}
      data-testid="phase-tab"
      className={cn(
        'group flex items-center gap-0.5 rounded-md border pr-0.5 pl-2',
        // The band's own colours, so a tab reads as the band it names.
        phaseAccentClass(accentIndex),
        'border-phase-accent/25 bg-phase-accent/12',
        // Selection deepens the tab's own colour rather than drawing a ring
        // round it — the surface says "picked" everywhere else (board cards,
        // graph nodes), and rings are kept for focus. The hue stays the
        // phase's, so the tab still reads as the band it names, only more so.
        selected && 'border-phase-accent/70 bg-phase-accent/28 shadow-rest',
        // A pointer that can drag must not also scroll the strip under it.
        drag && 'touch-none',
        drag?.isDragging && 'z-10 opacity-80 shadow-rest',
      )}
    >
      <button
        type="button"
        className="text-phase-accent focus-visible:ring-ring rounded-sm py-1 text-xs font-medium focus-visible:ring-3 focus-visible:outline-none"
        onClick={onSelect}
      >
        {phase.name}
      </button>
      <Button
        variant="ghost"
        size="icon-sm"
        // Quiet until wanted, but never gone: still rendered, still in tab
        // order, and revealed by focus as well as hover.
        className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        aria-label={`Move ${phase.name} earlier`}
        disabled={index === 0}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => onMove(index - 1)}
      >
        <ChevronLeftIcon />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        aria-label={`Move ${phase.name} later`}
        disabled={index === count - 1}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => onMove(index + 1)}
      >
        <ChevronRightIcon />
      </Button>
    </span>
  );
}

/** The strip around the tabs: its label, its holding pen, its "add". */
export function PhaseStripFrame({
  selection,
  onSelect,
  children,
}: PhaseStripProps & { children: ReactNode }) {
  const { draft, edit, ordered, unassigned } = usePhaseStripPhases();

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="builder-phase-strip"
    >
      <span className="text-muted-foreground mr-1 text-xs font-medium tracking-wide uppercase">
        Phases
      </span>

      {ordered.length === 0 && (
        // Said as a fact about what happens next, not as a warning. Steps used
        // to be parked in a holding pen until an author made a phase, and this
        // line existed to explain that state; there is no such state now, so it
        // explains the behaviour instead.
        <span className="text-muted-foreground text-xs">
          None yet — the first step you add creates one.
        </span>
      )}

      {children}

      {/*
        Listed after the ordered phases and without reorder controls: the
        holding pen is pinned to the trailing edge and is not a stage of the
        process (FR-041). It sits outside the sortable set entirely, so
        "unassigned is always last" holds by construction — there is no drop
        that could express otherwise for the model to have to refuse.
      */}
      {unassigned && unassigned.steps.length > 0 && (
        <button
          type="button"
          className={cn(
            'border-border text-muted-foreground focus-visible:ring-ring rounded-md border border-dashed px-2 py-1 text-xs focus-visible:ring-3 focus-visible:outline-none',
            selection?.kind === 'phase' &&
              selection.id === UNASSIGNED_PHASE_ID &&
              'border-primary bg-accent',
          )}
          onClick={() => onSelect({ kind: 'phase', id: UNASSIGNED_PHASE_ID })}
        >
          Unassigned ({unassigned.steps.length})
        </button>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          // Asked for rather than assumed: the derivation uniquifies, so the
          // second "New phase" is `new-phase-2`.
          const id = nextPhaseId(draft);
          edit(addPhase());
          // Selecting it puts the cursor where the user's next action is:
          // naming the thing they just made.
          onSelect({ kind: 'phase', id });
        }}
      >
        <PlusIcon />
        Add phase
      </Button>
    </div>
  );
}

/**
 * The strip without dragging — reorder by the arrows alone. Rendered on its own
 * as the fallback while the sortable chunk loads, so the tabs are usable on
 * first paint rather than replaced by a skeleton.
 */
export function PhaseStrip({ selection, onSelect }: PhaseStripProps) {
  const { edit, ordered } = usePhaseStripPhases();
  const accentOf = usePhaseAccents();

  return (
    <PhaseStripFrame selection={selection} onSelect={onSelect}>
      {ordered.map((phase, index) => (
        <PhaseTab
          key={phase.id}
          phase={phase}
          index={index}
          count={ordered.length}
          accentIndex={accentOf.get(phase.id) ?? 0}
          selected={selection?.kind === 'phase' && selection.id === phase.id}
          onSelect={() => onSelect({ kind: 'phase', id: phase.id })}
          onMove={(toIndex) => edit(reorderPhase(phase.id, toIndex))}
        />
      ))}
    </PhaseStripFrame>
  );
}
